import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Warning,
  Error as ErrorIcon,
  Info,
  CheckCircle,
  Delete,
  Visibility,
  MarkEmailRead,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import alertService from '../../services/alertService';
import websocketService from '../../services/websocketService';

const priorityConfig = {
  critical: { icon: ErrorIcon, color: 'error', bgColor: '#ffebee' },
  warning: { icon: Warning, color: 'warning', bgColor: '#fff3e0' },
  info: { icon: Info, color: 'info', bgColor: '#e3f2fd' },
};

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await alertService.getAlerts();
      // Handle both paginated ({results: []}) and non-paginated responses
      const alertsData = response.data.results || response.data;
      setAlerts(Array.isArray(alertsData) ? alertsData : []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();

    // WebSocket listeners
    const handleNewAlert = (alert) => {
      setAlerts((prev) => [alert, ...prev]);
    };

    const handleAlertUpdate = (alert) => {
      setAlerts((prev) =>
        prev.map((a) => (a.id === alert.id ? alert : a)).filter((a) => !a.is_dismissed)
      );
    };

    websocketService.on('alertNew', handleNewAlert);
    websocketService.on('alertUpdate', handleAlertUpdate);

    return () => {
      websocketService.off('alertNew', handleNewAlert);
      websocketService.off('alertUpdate', handleAlertUpdate);
    };
  }, [fetchAlerts]);

  const handleMarkRead = async (id) => {
    try {
      await alertService.markRead(id);
      setAlerts((prev) =>
        prev.map((a) => (a.id === id ? { ...a, is_read: true } : a))
      );
    } catch (error) {
      console.error('Failed to mark alert as read:', error);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await alertService.dismiss(id);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await alertService.markAllRead();
      setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNavigate = (alert) => {
    if (!alert.is_read) {
      handleMarkRead(alert.id);
    }
    if (alert.action_url) {
      navigate(alert.action_url);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const unreadCount = alerts.filter((a) => !a.is_read).length;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">
            Attention Required
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                color="error"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
          {unreadCount > 0 && (
            <Button
              startIcon={<MarkEmailRead />}
              onClick={handleMarkAllRead}
              variant="outlined"
            >
              Mark All Read
            </Button>
          )}
        </Box>

        {alerts.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6">All Clear!</Typography>
            <Typography color="text.secondary">
              No alerts requiring attention.
            </Typography>
          </Paper>
        ) : (
          <Paper>
            <List>
              {alerts.map((alert) => {
                const config = priorityConfig[alert.priority] || priorityConfig.warning;
                const PriorityIcon = config.icon;

                return (
                  <ListItem
                    key={alert.id}
                    sx={{
                      bgcolor: alert.is_read ? 'inherit' : config.bgColor,
                      borderLeft: '4px solid',
                      borderLeftColor: `${config.color}.main`,
                      cursor: alert.action_url ? 'pointer' : 'default',
                      '&:hover': {
                        bgcolor: alert.is_read ? 'action.hover' : config.bgColor,
                      },
                    }}
                    onClick={() => handleNavigate(alert)}
                  >
                    <ListItemIcon>
                      <PriorityIcon color={config.color} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          fontWeight={alert.is_read ? 'normal' : 'bold'}
                        >
                          {alert.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(alert.created_at), 'dd MMM yyyy HH:mm')}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      {!alert.is_read && (
                        <Tooltip title="Mark as read">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkRead(alert.id);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Dismiss">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDismiss(alert.id);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default Alerts;
